import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import {
  CalendarInputComponent,
  CheckboxInputComponent,
  NumberInputComponent,
  SelectInputComponent,
  TextInputComponent,
} from '@navstation-portal/shared/ui/components';
import {
  StateField,
  StateFieldType,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '../form-field/form-field.component';

type ColumnType =
  | 'datepicker'
  | 'autocomplete'
  | 'text'
  | 'number'
  | 'select'
  | 'checkbox'
  | 'rowTitle';

export interface TableColumn<T> {
  field: string;
  header: string;
  type: ColumnType;
  readOnly?: boolean;
  disabled?: boolean;
  title?: string;
  subtitleField?: string;
  options?: SelectOption[] | undefined;
  bgColorField?: string;
  minWidth?: string;
  hideFunction?: (rowData: T) => boolean;
  requiredFunction?: (rowData: T) => boolean;
}

@Component({
  selector: 're-stock-table',
  imports: [
    CommonModule,
    CheckboxModule,
    FormsModule,
    InputNumberModule,
    SelectModule,
    AutoCompleteModule,
    DatePickerModule,
    TextInputComponent,
    NumberInputComponent,
    SelectInputComponent,
    CheckboxInputComponent,
    CalendarInputComponent,
  ],
  templateUrl: './stock-table.component.html',
  styleUrl: './stock-table.component.css',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => {
        const parentForm = inject(NgForm, { skipSelf: true, optional: true });
        return parentForm;
      },
    },
  ],
})
export class StockTableComponent<
  T extends Record<string, StateField<StateFieldType>>,
> {
  data = input<T[]>();
  tabId = input.required<string>();
  sectionId = input.required<string>();
  fieldId = input.required<string>();
  columns = input.required<TableColumn<T>[]>();
  required = input<boolean>(false);
  // For displaying calculated values (read-only)
  tankNameField = input<string>('tankName');
  private filteredOptionsMap = new Map<keyof T, SelectOption[]>();

  private getWeightedFlex(minWidth?: string): string {
    if (!minWidth) return '1fr';

    // Weighted flex lets wider columns claim more free space without fixing size.
    // Keep weights proportional so e.g. 40px < 70px < 150px in allocated flex,
    // while ensuring each width has enough growth headroom beyond its minimum.
    const pxMatch = minWidth.match(/^\s*(\d+(?:\.\d+)?)px\s*$/i);
    if (pxMatch) {
      const px = Number(pxMatch[1]);
      const weight = Math.max(1, px / 40);
      return `${Number(weight.toFixed(2))}fr`;
    }

    const remMatch = minWidth.match(/^\s*(\d+(?:\.\d+)?)rem\s*$/i);
    if (remMatch) {
      const rem = Number(remMatch[1]);
      const px = rem * 16;
      const weight = Math.max(1, px / 40);
      return `${Number(weight.toFixed(2))}fr`;
    }

    return '1fr';
  }

  get gridColumns(): string {
    return this.columns()
      .map((col) =>
        col.minWidth
          ? `minmax(${col.minWidth}, ${this.getWeightedFlex(col.minWidth)})`
          : 'minmax(0, 1fr)',
      )
      .join(' ');
  }

  getFilteredOptions(field: keyof T): SelectOption[] {
    return this.filteredOptionsMap.get(field) || [];
  }

  filter(
    event: AutoCompleteCompleteEvent,
    options: SelectOption[],
    field: keyof T,
  ) {
    const query = event.query.toLowerCase();
    const filtered = options.filter((option) =>
      option.name.toLowerCase().includes(query),
    );
    this.filteredOptionsMap.set(field, filtered);
  }

  convertStringToDate(dateString: StateFieldType): Date | undefined {
    const parsedDate = Date.parse(dateString as string);
    if (isNaN(parsedDate)) {
      return undefined;
    }
    const date = new Date(parsedDate);
    return isNaN(date.getTime()) ? undefined : date;
  }

  assertAsSelectOption(value: StateFieldType): SelectOption | undefined {
    if (
      value &&
      typeof value === 'object' &&
      'code' in value &&
      'name' in value
    ) {
      return value as SelectOption;
    }
    return undefined;
  }

  assertAsDate(value: StateFieldType): Date | undefined {
    if (value instanceof Date) {
      return value;
    }
    return undefined;
  }
}
